"use client";
import { useEffect, useState } from "react";
import type { DesignAgentConnections } from "@open-design/contracts";
import { platform, type DesignSession } from "./api";
import { Button, Input } from "./ui";
import css from "./BuildyWorkspace.module.css";
import { WebsiteAgentGuidance } from "./WebsiteAgentGuidance";

const modes: Record<string, string> = {
  browser: "Browser edit",
  manual: "Manual edit",
  cloud_ai: "Buildy AI",
  external_agent: "External agent",
};
export function WebsiteAgentConnections({
  websiteId,
  org,
  session,
  canEdit,
}: {
  websiteId: string;
  org: number;
  session: DesignSession;
  canEdit: boolean;
}) {
  const [data, setData] = useState<DesignAgentConnections | null>(null),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false),
    [label, setLabel] = useState("My local agent"),
    [token, setToken] = useState(""),
    [copied, setCopied] = useState(false);
  const path = `websites/${websiteId}/agent-connections/`;
  async function refresh() {
    try {
      setData(await platform<DesignAgentConnections>(path, { org }));
      setError("");
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "Could not load agent connections.",
      );
    }
  }
  useEffect(() => {
    const controller = new AbortController();
    const load = () => {
      void platform<DesignAgentConnections>(path, {
        org,
        signal: controller.signal,
      })
        .then((value) => {
          if (!controller.signal.aborted) {
            setData(value);
            setError("");
          }
        })
        .catch((e) => {
          if (!controller.signal.aborted) setError(e.message);
        });
    };
    setToken("");
    setData(null);
    load();
    const timer = setInterval(load, 5000);
    return () => {
      controller.abort();
      clearInterval(timer);
    };
  }, [path, org]);
  async function connect() {
    if (busy) return;
    setBusy(true);
    try {
      const value = await platform<{ token: string }>(path, {
        method: "POST",
        org,
        csrf: session.csrf_token,
        body: { label, scopes: ["read", "edit", "cloud_ai"] },
      });
      setToken(value.token);
      setCopied(false);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not connect agent.");
    } finally {
      setBusy(false);
    }
  }
  async function revoke(id: string) {
    setBusy(true);
    try {
      await platform(path, {
        method: "DELETE",
        org,
        csrf: session.csrf_token,
        body: { connection_id: id },
      });
      setToken("");
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not revoke connection.");
    } finally {
      setBusy(false);
    }
  }
  const sessions = data?.sessions ?? [];
  const submitted = new Set(
    sessions.map((item) => item.change_id).filter(Boolean),
  );
  const activity = [
    ...sessions,
    ...(data?.changes ?? []).filter((item) => !submitted.has(item.id)),
  ].slice(0, 15);
  return (
    <>
      <section className={css.panel} aria-label="Agent connections">
        <div className={css.panelHeading}>
          <h2>Agent connections</h2>
        </div>
        <div className={css.panelBody}>
          <p>
            Connect your own agent to this website. Source submissions create a
            checked preview; use Save changes in Buildy to publish.
          </p>
          {canEdit && (
            <form
              onSubmit={(event) => {
                event.preventDefault();
                void connect();
              }}
            >
              <label>
                Agent name
                <Input
                  value={label}
                  maxLength={80}
                  required
                  disabled={busy}
                  onChange={(event) => setLabel(event.target.value)}
                />
              </label>
              <Button type="submit" disabled={busy || !label.trim()}>
                Connect agent
              </Button>
            </form>
          )}
          {token && (
            <div>
              <label>
                One-time connection token
                <Input
                  type="password"
                  readOnly
                  value={token}
                  autoComplete="off"
                />
              </label>
              <Button
                onClick={() => {
                  void navigator.clipboard
                    .writeText(token)
                    .then(() => setCopied(true));
                }}
              >
                {copied ? "Copied" : "Copy token"}
              </Button>
              <p>
                Store this token in your local agent environment. It expires
                after 24 hours. Buildy will not show it again after you leave
                this page.
              </p>
            </div>
          )}
          {error && <p role="alert">{error}</p>}
          {!data && !error && <p role="status">Loading agent connections…</p>}
          {data?.connections.map((connection) => (
            <div className={css.agentConnection} key={connection.id}>
              <div>
                <strong>{connection.label}</strong>
                <p>
                  {connection.active
                    ? "Connected"
                    : connection.revoked
                      ? "Revoked"
                      : "Expired"}{" "}
                  · {connection.scopes.join(", ")} · Expires{" "}
                  {new Date(connection.expires_at).toLocaleString()}
                </p>
              </div>
              {connection.active && canEdit && (
                <Button
                  disabled={busy}
                  onClick={() => void revoke(connection.id)}
                >
                  Revoke
                </Button>
              )}
            </div>
          ))}
          <WebsiteAgentGuidance
            key={`${org}:${websiteId}`}
            websiteId={websiteId}
            org={org}
          />
        </div>
      </section>
      <section
        className={`${css.panel} ${css.pagesPanel}`}
        aria-label="Website execution activity"
      >
        <div className={css.panelHeading}>
          <h2>Recent work</h2>
        </div>
        <div className={css.pagesTableScroll}>
          <table className={css.pagesTable}>
            <thead>
              <tr>
                <th>Execution</th>
                <th>Client</th>
                <th>Status</th>
                <th>Source</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {activity.map((item) => (
                <tr key={item.id}>
                  <th scope="row">{modes[item.mode] ?? item.mode}</th>
                  <td>{item.client || "Buildy"}</td>
                  <td>
                    {item.status.replaceAll("_", " ").toLowerCase()}
                    {item.error_code && <span> · {item.error_code}</span>}
                  </td>
                  <td>
                    {item.base_source_version
                      ? `Version ${item.base_source_version}`
                      : "—"}
                  </td>
                  <td>
                    {item.preview_url && (
                      <a
                        className={`${css.pageEdit} ${css.agentPreview}`}
                        href={item.preview_url}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Preview
                      </a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {data && !activity.length && (
          <p className={css.panelBody}>No work has been recorded yet.</p>
        )}
      </section>
    </>
  );
}
